import { useState, useEffect } from 'react'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import { toast } from 'sonner'
import { isNative, openWithNativeViewer } from '@/lib/native-file-viewer'

import 'react-photo-view/dist/react-photo-view.css'
import { Eye } from '@/components/icons'
import { Button } from './ui/button'
import { useS3GetDownloadUrl } from '@/lib/hooks/s3'

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg']
const PDF_EXTENSIONS = ['pdf']

function getFileExtension(fileName: string): string {
  return (fileName.split('.').pop() || '').toLowerCase()
}

interface DocumentViewerProps {
  s3Key: string | null | undefined
  fileName: string | null | undefined
}

export function DocumentViewer({ s3Key, fileName }: DocumentViewerProps) {
  const [url, setUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [opening, setOpening] = useState(false)
  const getDownloadUrl = useS3GetDownloadUrl()

  const ext = getFileExtension(fileName || s3Key || '')
  const isImage = IMAGE_EXTENSIONS.includes(ext)
  const isPdf = PDF_EXTENSIONS.includes(ext)
  const isViewable = isImage || isPdf

  const fetchUrl = async (): Promise<string | null> => {
    if (url) return url
    if (!s3Key) return null
    setLoading(true)
    try {
      const { downloadUrl } = await getDownloadUrl.mutateAsync({ s3Key })
      setUrl(downloadUrl)
      return downloadUrl
    } catch {
      return null
    } finally {
      setLoading(false)
    }
  }

  const handleView = async () => {
    const viewUrl = await fetchUrl()
    if (!viewUrl) {
      toast.error("Couldn't open this document.")
      return
    }

    setOpening(true)
    try {
      // Native: temp download + system viewer. Web: new tab.
      const handled = await openWithNativeViewer(viewUrl, fileName || s3Key || undefined)
      if (handled) return
      window.open(viewUrl, '_blank')
    } catch (error) {
      console.error('Failed to open document:', error)
      toast.error("Couldn't open this document.")
    } finally {
      setOpening(false)
    }
  }

  // Web images: fetch URL eagerly so PhotoView has the src ready
  useEffect(() => {
    if (isImage && !isNative && s3Key && !url) {
      fetchUrl()
    }
  }, [s3Key, isImage])

  if (!s3Key || !isViewable) return null

  if (isImage && !isNative) {
    return (
      <PhotoProvider>
        <PhotoView src={url || ''}>
          <Button variant="primary" loading={loading} disabled={!url}>
            <Eye />
            View
          </Button>
        </PhotoView>
      </PhotoProvider>
    )
  }

  // Native (images + PDFs) and web PDFs: both use handleView
  return (
    <Button
      variant="primary"
      onClick={handleView}
      loading={loading || opening}
      className="w-full"
    >
      <Eye />
      View
    </Button>
  )
}
